import axios from "axios";
import { getUserToken } from "../../hooks/authFunctions";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_MESSENGER_API_URL,
  headers: { "Content-Type": "application/json" },
});

const withAuth = async () => {
  const user = await getUserToken();
  const token = user.idToken.toString();
  axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

// roomId scopes the connection token's auto-subscribed channel to just that room — the
// backend no longer indexes "all of this agent's rooms," so a token request with no roomId
// gets no live channels (fine for callers that only need the token itself).
export const fetchMessengerToken = async (roomId) => {
  await withAuth();
  const { data } = await axiosInstance.get("/messenger/token", {
    params: roomId ? { jobNumber: roomId } : undefined,
  });
  return data.token;
};

export const createRoom = async (roomPayload) => {
  await withAuth();
  const { data } = await axiosInstance.post("/messenger/rooms", roomPayload);
  return data;
};

// Returns { jobNumber, count, rooms: [{ channelName, ... }] } scoped to this
// job only — no need to list/filter every room across every job.
export const fetchRoomsByJobNumber = async (jobNumber) => {
  await withAuth();
  const { data } = await axiosInstance.get(`/messenger/rooms/${jobNumber}`);
  return data;
};

// One row per job with a conversation, most-recent-first, optionally narrowed to one
// carrier and/or a job number. With neither filter this is every conversation across every
// carrier — used both by the standalone Message Center page (global) and the carrier edit
// page's embedded list (carrierOrg-scoped).
export const fetchMessageThreads = async (carrierOrg, jobNumber) => {
  await withAuth();
  const { data } = await axiosInstance.get("/messenger/threads", {
    params: {
      ...(carrierOrg ? { carrierOrg } : {}),
      ...(jobNumber ? { jobNumber } : {}),
    },
  });
  return data.threads;
};

export const fetchMessages = async (roomId) => {
  await withAuth();
  const { data } = await axiosInstance.get("/messenger/messages", {
    params: { roomId },
  });
  return data;
};

// Auto-provisions the room for this job (if it doesn't exist yet) and
// delivers the message in a single call. Used for both starting a new
// conversation and sending subsequent messages.
export const sendMessageForJob = async (jobNumber, { carrierAgentId, carrierOrg, body }) => {
  await withAuth();
  const { data } = await axiosInstance.post(`/messenger/rooms/${jobNumber}/messages`, {
    carrierAgentId,
    carrierOrg,
    body
  });
  return data;
};

export const subscribeToPush = async (subscription) => {
  await withAuth();
  const { data } = await axiosInstance.post("/messenger/push/subscribe", { subscription });
  return data;
};

export const unsubscribeFromPush = async (endpoint) => {
  await withAuth();
  const { data } = await axiosInstance.delete("/messenger/push/subscribe", {
    params: { endpoint },
  });
  return data;
};

export const fetchUnread = async () => {
  await withAuth();
  const { data } = await axiosInstance.get("/messenger/unread");
  return data;
};

export const markRead = async (channel) => {
  await withAuth();
  const { data } = await axiosInstance.post("/messenger/read", { channel });
  return data;
};
